import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import SettingsToggle from '../../controls/settings-toggle';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getIsSettingsUpdating,
  getWorkingProjectUuid,
  selectIsSavingWorkingProject,
  selectProjectSettings,
} from '@/store/slices/project/selectors';
import { updateProjectSettings, updatePublishSettings } from '@/store/slices/project';
import { selectCookieBanner, selectIsPublishLocked } from '@/store/slices/settings/selectors';
import { EventSettings } from '@/types/projects.types';
import ProductIdWidget from './product-id-widget';
import SessionMetadataWidget from './session-metadata-widget';
import { ChannelFeaturesEnum } from '@/types/feature-flags.types';
import useCanUseChannelFeature from '@/utils/use-can-use-channel-features';
import RedirectUrlWidget from './redirect-url-widget';
import MarketingEmailWidget from './marketing-email-widget';
import PageTranslationsWidget from './page-translations-widget';
import { ESettingsAdvancedWidgetLabels } from '../../settings.types';

const AdvancedSettings = () => {
  const cookieBannerEnabled = useAppSelector(selectCookieBanner);
  const isPublishLocked = useAppSelector(selectIsPublishLocked);
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);
  const isSettingsUpdating = useAppSelector(getIsSettingsUpdating);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const projectSettings = useAppSelector(selectProjectSettings);

  const publishLockEnabled = useCanUseChannelFeature(ChannelFeaturesEnum.event_publish_locking);

  const dispatch = useAppDispatch();

  const handleCookieBannerToggle = (val: string, on: boolean) => {
    if (projectUuid && projectSettings) {
      const settings: EventSettings = {
        ...projectSettings,
        gdpr: {
          ...projectSettings.gdpr,
          enable_cookie_banner: on,
        },
      };
      dispatch(updateProjectSettings({ event_uuid: projectUuid, settings }));
    }
  };

  const handlePublishLockToggle = (val: string, on: boolean) => {
    if (projectUuid) {
      dispatch(
        updatePublishSettings({
          project_uuid: projectUuid,
          isLocked: on,
        })
      );
    }
  };

  return (
    <div className="flex-column gap-8">
      <PageTranslationsWidget />
      <SettingsToggle
        label={ESettingsAdvancedWidgetLabels.PUBLISH_LOCKING}
        value={isPublishLocked}
        sKey="publish-lock"
        disabled={!publishLockEnabled || savingWorkingProject || isSettingsUpdating}
        onToggle={handlePublishLockToggle}
      />
      <ProductIdWidget />
      <SessionMetadataWidget />
      <RedirectUrlWidget />
      <div className="flex gap-4">
        <Text className="H5" design="GlobalIcoText1">
          GDPR
        </Text>
        <Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--global-ico-text-3)" />
      </div>
      <SettingsToggle
        label={ESettingsAdvancedWidgetLabels.COOKIE_BANNER}
        value={cookieBannerEnabled}
        sKey="cookie"
        disabled={savingWorkingProject || isSettingsUpdating}
        onToggle={handleCookieBannerToggle}
      />
      <MarketingEmailWidget />
    </div>
  );
};

export default AdvancedSettings;
